import { isDigit } from '../utils';
import type { ParseRawStringParams } from '../types';

// helper functions for the algorithm below
const isGroupText = ([, num]: [string, number]) => num === -1;
const isGroupDigitWithoutDecimalSeparator = ([, num]: [string, number]) => num === -2;

// Idea:
//   Label each character of `rawString` with either TEXT or DIGIT. Divide `rawString` into
//   groups (i.e. substrings) of adjacent characters with the same label. Output the information
//   of each group.
//
// Labeling rules:
//   - if the character is "0", "1", ..., "9", then label it with DIGIT;
//   - if the character is `decimalSeparator`:
//       - if it is right between two characters of label DIGIT, then label it with DIGIT;
//       - otherwise, label it with TEXT;
//   - for any other characters: label them with TEXT.
//
// Information of a substring:
//   - [substring, -1] if the substring consists of characters of label TEXT
//   - [substring, number of decimal places] if the substring consists of characters of label DIGIT
//
// Example (`decimalSeparator` being "."):
//   "abc12.3@45,6.78.9.0" =>
//   [
//     ["abc", -1],
//     ["12.3", 1],
//     ["@", -1],
//     ["45", 0],
//     [",", -1],
//     ["6.78", 2],
//     [".", -1],
//     ["9.0", "1"],
//   ]
export const parseRawString = ({
  rawString,
  decimalSeparatorForNumberDetection,
}: ParseRawStringParams): [string, number][] => {
  const arr = [...rawString];
  const output: [string, number][] = [];

  if (arr.length === 0) {
    return output;
  }

  // init `latestGroup`
  // -2 temporarily indicates a group DIGIT with no decimal separator included
  let latestGroup: [string, number] = [arr[0], isDigit(arr[0]) ? -2 : -1];

  for (let i = 1; i < arr.length; i += 1) {
    const v = arr[i];

    if (isGroupText(latestGroup)) {
      if (isDigit(v)) {
        output.push([...latestGroup]);
        latestGroup = [v, -2];
      } else {
        latestGroup[0] += v;
      }
    } else if (isDigit(v)) {
      latestGroup[0] += v;
      if (!isGroupDigitWithoutDecimalSeparator(latestGroup)) latestGroup[1] += 1;
    } else if (v === decimalSeparatorForNumberDetection) {
      if (!isGroupDigitWithoutDecimalSeparator(latestGroup)) {
        output.push([...latestGroup]);
        latestGroup = [v, -1];
      } else if (i < arr.length - 1 && isDigit(arr[i + 1])) {
        latestGroup[0] += v;
        latestGroup[1] = 0;
      } else {
        latestGroup[1] = 0;
        output.push([...latestGroup]);
        latestGroup = [v, -1];
      }
    } else {
      if (isGroupDigitWithoutDecimalSeparator(latestGroup)) {
        latestGroup[1] = 0;
      }
      output.push([...latestGroup]);
      latestGroup = [v, -1];
    }
  }

  if (isGroupDigitWithoutDecimalSeparator(latestGroup)) {
    latestGroup[1] = 0;
  }

  output.push([...latestGroup]);

  return output;
};
